import { Facebook, Twitter, Linkedin, Mail, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { APP_CONFIG } from '@/config/appConfig';

interface SocialShareButtonsProps {
  /** Canonical URL shown to humans (email body, tooltip). */
  canonicalUrl: string;
  /** Edge-function URL that returns proper OG tags for crawlers. */
  ogUrl: string;
  /** Title used in Twitter/email subject. */
  title: string;
  /** Optional pre-label ("Share:" etc). */
  label?: string;
  className?: string;
}

/**
 * Renders explicit per-platform share buttons that open in the CURRENT browser
 * via window.open, bypassing the OS share sheet (which on Windows always
 * launches Edge and passes the raw canonical URL, breaking Facebook previews).
 *
 * Social platforms scrape `ogUrl` (Supabase edge function) so previews render
 * even when the Cloudflare Worker crawler path is misbehaving on canonical URLs.
 */
export const SocialShareButtons = ({
  canonicalUrl,
  ogUrl,
  title,
  label = 'Share:',
  className,
}: SocialShareButtonsProps) => {
  const encodedOgUrl = encodeURIComponent(ogUrl);
  const encodedTitle = encodeURIComponent(title);

  const openShareWindow = (url: string) => {
    window.open(url, '_blank', 'width=600,height=500,scrollbars=yes');
  };

  const handleFacebook = () =>
    openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodedOgUrl}`);
  const handleTwitter = () =>
    openShareWindow(`https://twitter.com/intent/tweet?url=${encodedOgUrl}&text=${encodedTitle}`);
  const handleLinkedIn = () =>
    openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedOgUrl}`);
  const handleEmail = () => {
    window.location.href = `mailto:?subject=${encodedTitle}&body=${encodeURIComponent(
      `Check this out: ${canonicalUrl}`,
    )}`;
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(ogUrl);
      toast.success('Link copied to clipboard!');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = ogUrl;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        toast.success('Link copied to clipboard!');
      } catch {
        toast.error('Copy failed. Please copy the URL manually.');
      }
      document.body.removeChild(ta);
    }
  };

  const buttons = [
    { icon: Facebook, label: 'Share on Facebook', action: handleFacebook },
    { icon: Twitter, label: 'Share on X', action: handleTwitter },
    { icon: Linkedin, label: 'Share on LinkedIn', action: handleLinkedIn },
    { icon: Mail, label: 'Share via Email', action: handleEmail },
    { icon: LinkIcon, label: 'Copy link', action: handleCopy },
  ];

  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`}>
      {label && <span className="text-sm text-muted-foreground">{label}</span>}
      <div className="flex items-center gap-1">
        {buttons.map(({ icon: Icon, label: btnLabel, action }) => (
          <Button
            key={btnLabel}
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            onClick={action}
            aria-label={btnLabel}
            type="button"
          >
            <Icon className="h-4 w-4" />
          </Button>
        ))}
      </div>
    </div>
  );
};

interface PropertyShareButtonsProps {
  propertyId: string;
  sellerId: string;
  propertyAddress?: string;
  label?: string;
  className?: string;
}

export const PropertyShareButtons = ({
  propertyId,
  sellerId,
  propertyAddress,
  label,
  className,
}: PropertyShareButtonsProps) => {
  const canonicalUrl = `${APP_CONFIG.PRODUCTION_URL}/public/seller/${sellerId}/property/${propertyId}`;
  const ogUrl = `${APP_CONFIG.SUPABASE_FUNCTIONS_URL}/og-property-metadata?propertyId=${propertyId}&sellerId=${sellerId}`;
  const title = propertyAddress
    ? `Check out this property on GoDyspo: ${propertyAddress}`
    : 'Check out this property on GoDyspo';
  return (
    <SocialShareButtons
      canonicalUrl={canonicalUrl}
      ogUrl={ogUrl}
      title={title}
      label={label}
      className={className}
    />
  );
};

interface SellerShareButtonsProps {
  /** Seller slug OR UUID — whichever the current route uses. */
  sellerParam: string;
  sellerName?: string;
  label?: string;
  className?: string;
}

export const SellerShareButtons = ({
  sellerParam,
  sellerName,
  label,
  className,
}: SellerShareButtonsProps) => {
  const canonicalUrl = `${APP_CONFIG.PRODUCTION_URL}/public/seller/${sellerParam}`;
  const ogUrl = `${APP_CONFIG.SUPABASE_FUNCTIONS_URL}/og-seller-metadata?sellerParam=${encodeURIComponent(
    sellerParam,
  )}`;
  const title = sellerName
    ? `${sellerName}'s investment properties on GoDyspo`
    : 'Investment properties on GoDyspo';
  return (
    <SocialShareButtons
      canonicalUrl={canonicalUrl}
      ogUrl={ogUrl}
      title={title}
      label={label}
      className={className}
    />
  );
};
